import { USAGES, isValidRatio, mapRatioTable, periodsOf, setUsageRatio } from './domain';
import { isValidPerf, perfOf, perfTableOf, setPerf } from './perf';
import { parseClipboard, parseNum } from './sheet';
import type { EnergyKey, RatioTable } from './types';

export function restoreRatioEnergy(value: RatioTable, snapshot: RatioTable, energy: EnergyKey): RatioTable {
  const performance = perfTableOf(value);
  const savedPerformance = perfTableOf(snapshot);
  return {
    ...value,
    ...mapRatioTable((u, p) => ({ ...value[u][p], [energy]: snapshot[u][p][energy] })),
    performance: mapRatioTable((u, p) => ({ ...performance[u][p], [energy]: savedPerformance[u][p][energy] })),
  };
}

export function copyRatioColumn(value: RatioTable, energy: EnergyKey, column: number): string {
  const periods = periodsOf(value);
  if (!Number.isInteger(column) || column < 0 || column > periods.length) throw new Error('Colonne inconnue.');
  return USAGES.map(u => {
    const number = column === 0 ? value[u.key][periods[0].key][energy] : perfOf(value, u.key, periods[column - 1].key, energy);
    return number === null ? '' : String(number).replace('.', ',');
  }).join('\n');
}

export function copyRatioTable(value: RatioTable, energy: EnergyKey, calculated = false): string {
  const periods = periodsOf(value);
  const format = (number: number | null) => number === null ? '' : String(number).replace('.', ',');
  const header = ['Typologie', 'Ratio (kWh/m²/an)', ...periods.map(p => `${p.label} (${calculated ? 'kWh/m²/an' : '%'})`)];
  return [header, ...USAGES.map(u => [
    u.label, format(value[u.key][periods[0].key][energy]),
    ...periods.map(p => format(calculated ? value[u.key][p.key][energy] : perfOf(value, u.key, p.key, energy))),
  ])].map(row => row.join('\t')).join('\n');
}

export function pasteRatioColumn(value: RatioTable, energy: EnergyKey, column: number, text: string) {
  const matrix = parseClipboard(text);
  if (matrix.some(row => row.length !== 1)) throw new Error('Sélectionnez et copiez une seule colonne de valeurs, sans en-tête.');
  return pasteRatioRange(value, energy, 0, column, text);
}

export function pasteRatioRange(value: RatioTable, energy: EnergyKey, row: number, column: number, text: string) {
  const matrix = parseClipboard(text);
  const periods = periodsOf(value);
  const width = matrix[0]?.length ?? 0;
  if (!width || matrix.some(line => line.length !== width)) throw new Error('Copiez une plage rectangulaire de valeurs.');
  if (row < 0 || column < 0 || row + matrix.length > USAGES.length || column + width > periods.length + 1) {
    throw new Error('La plage dépasse ce tableau. Aucun autre poste ne sera modifié.');
  }
  const numbers = matrix.map((line, r) => line.map((raw, c) => {
    const col = column + c;
    const number = parseNum(col > 0 ? raw.replace(/%\s*$/, '') : raw);
    const valid = col === 0 ? isValidRatio(number) : col === 1 ? number === 100 : isValidPerf(number);
    if (!valid) {
      const label = col === 0 ? 'ratio entre 0 et 1 000' : col === 1 ? 'référence verrouillée à 100 %' : 'coefficient entre 1 et 100 %';
      throw new Error(`${USAGES[row + r].label}, ${col === 0 ? 'ratio de référence' : periods[col - 1].label} : ${label}. Aucune valeur collée.`);
    }
    return number;
  }));
  let ratios = value;
  const keys: string[] = [];
  numbers.forEach((line, r) => line.forEach((number, c) => {
    const usage = USAGES[row + r].key;
    const col = column + c;
    if (col === 0) {
      ratios = setUsageRatio(ratios, usage, energy, number);
      keys.push(`ratio.${usage}.${energy}`);
    } else {
      const period = periods[col - 1].key;
      ratios = setPerf(ratios, usage, period, energy, number);
      keys.push(`${usage}.${period}.${energy}`);
    }
  }));
  return { ratios, keys, count: matrix.length * width };
}
